import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { fetchSalesOrdersRollingDates } from "../../utils/api/reportApi";
import {
  Section,
  Row,
  StatCard,
  HeroCard,
  pct,
  money,
  fmtNumber,
} from "./ReportUIPrimitives";
import styles from "./SalesOrdersRollingViewer.module.css";

const QUICK_WINDOW_KEYS = [
  { key: "today",        label: "Today" },
  { key: "yesterday",    label: "Yesterday" },
  { key: "last_7_days",  label: "7 days" },
  { key: "last_30_days", label: "30 days" },
  { key: "last_90_days", label: "90 days" },
];

export default function SalesOrdersRollingViewer({ data, report }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuthUser();
  const authToken = user?.token || null;
  const userId = report?.user_id || null;
  const shopId = report?.shop_id || null;
  const currentReportId = report?._id || report?.id || null;

  const [windows, setWindows] = useState([]);
  const initialQuickWindow = useMemo(() => {
    const requested =
      searchParams.get("quickWindow") ||
      location.state?.salesOrdersQuickWindow ||
      null;
    return QUICK_WINDOW_KEYS.some((entry) => entry.key === requested)
      ? requested
      : "last_90_days";
  }, [location.state?.salesOrdersQuickWindow, searchParams]);
  const [activeWindow, setActiveWindow] = useState(initialQuickWindow);

  useEffect(() => {
    setActiveWindow((current) =>
      current === initialQuickWindow ? current : initialQuickWindow
    );
  }, [initialQuickWindow]);

  useEffect(() => {
    if (!userId) return;
    fetchSalesOrdersRollingDates({ userId, shopId, authToken })
      .then((result) => {
        if (result?.ok && Array.isArray(result.dates)) setWindows(result.dates);
      })
      .catch(() => {});
  }, [userId, shopId, authToken]);

  // Resolve active window data — prefer quick_windows, fall back to top-level
  const quickWindows = data?.quick_windows || null;
  const activeWindowData = quickWindows?.[activeWindow] || null;

  const orders = activeWindowData?.orders || data?.orders || {};
  const customers = activeWindowData?.customers || data?.customers || {};
  const summary = activeWindowData?.summary || data?.summary || null;
  const reportWindow = data?.report_window || {};
  const month = data?.month || null;
  const currency =
    data?.shop?.currency || orders?.order_revenue_shop_currency || "USD";

  const totalCustomers = customers?.total_customers;
  const newCustomers = customers?.new_customers;
  const returningCustomers = customers?.returning_customers;
  const repeatPurchaseRate = customers?.repeat_purchase_rate;
  const averageOrdersPerCustomer = customers?.average_orders_per_customer;
  const topCustomerRevenueShare = customers?.top_10_percent_customer_revenue_share;

  const repeatPct =
    repeatPurchaseRate != null
      ? Math.min(Math.max(repeatPurchaseRate * 100, 0), 100)
      : 0;

  const newCustomerShare =
    totalCustomers != null && newCustomers != null && totalCustomers > 0
      ? (newCustomers / totalCustomers) * 100
      : null;

  const formatDecimal = (value, digits = 2) => {
    if (value == null) return "—";
    const num = typeof value === "number" ? value : parseFloat(value);
    if (Number.isNaN(num)) return "—";
    return num.toFixed(digits);
  };

  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

  const activeWindowMeta = quickWindows?.[activeWindow] || null;

  return (
    <div className={styles.container}>
      {/* Report-level window picker (historical reports) */}
      {(windows.length > 0 ||
        reportWindow.startDate ||
        reportWindow.endDate) && (
        <div className={styles.windowBar}>
          {windows.length > 0 ? (
            <select
              className={styles.windowPickerSelect}
              value={currentReportId || ""}
              onChange={(e) => {
                if (e.target.value)
                  globalThis.location.href = `/reports/${e.target.value}`;
              }}
              aria-label="Sales & Orders report window"
            >
              {windows.map((w) => (
                <option key={w.reportId} value={w.reportId}>
                  {w.label}
                </option>
              ))}
            </select>
          ) : reportWindow.label ? (
            <span className={styles.windowLabel}>{reportWindow.label}</span>
          ) : null}
          {reportWindow.startDate && (
            <span className={styles.windowDate}>{fmtDate(reportWindow.startDate)}</span>
          )}
          {reportWindow.startDate && reportWindow.endDate && (
            <span className={styles.windowSep}>→</span>
          )}
          {reportWindow.endDate && (
            <span className={styles.windowDate}>{fmtDate(reportWindow.endDate)}</span>
          )}
        </div>
      )}

      {/* Quick window tabs */}
      {quickWindows && (
        <div className={styles.quickWindowTabs}>
          {QUICK_WINDOW_KEYS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`${styles.quickWindowTab} ${activeWindow === key ? styles.quickWindowTabActive : ""}`}
              onClick={() => setActiveWindow(key)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Active window date range */}
      {activeWindowMeta && (
        <div className={styles.activeWindowRange}>
          <span className={styles.activeWindowLabel}>{activeWindowMeta.label}</span>
          {activeWindowMeta.startDate && activeWindowMeta.endDate && (
            <span className={styles.activeWindowDates}>
              {fmtDate(activeWindowMeta.startDate)}
              {" → "}
              {fmtDate(activeWindowMeta.endDate)}
            </span>
          )}
        </div>
      )}

      {summary && (
        <div className={styles.summaryCard}>
          <p className={styles.summaryLabel}>Summary</p>
          <p className={styles.summaryText}>{summary}</p>
        </div>
      )}

      <HeroCard title="Order Summary" badge={month}>
        <div className={styles.statsGrid}>
          <StatCard label="Orders" value={fmtNumber(orders?.orders_count)} />
          <StatCard
            label="Items Ordered"
            value={fmtNumber(orders?.items_ordered)}
          />
          <StatCard
            label="Returns"
            value={fmtNumber(orders?.returns_count)}
          />
          <StatCard
            label="Orders Fulfilled"
            value={fmtNumber(orders?.orders_fulfilled)}
          />
          <StatCard
            label="Orders Delivered"
            value={fmtNumber(orders?.orders_delivered)}
          />
          <StatCard
            label="Order Revenue"
            value={money(orders?.order_revenue_shop_money, currency)}
          />
          <StatCard
            label="Net Revenue (After Discounts)"
            value={money(
              orders?.net_order_revenue_after_discounts_shop_money,
              currency,
            )}
          />
        </div>
      </HeroCard>

      <Section title="Customers">
        <div className={styles.customerHeader}>
          <div>
            <h5 className={styles.customerTitle}>Customer Momentum</h5>
            <p className={styles.customerSubtitle}>
              {totalCustomers != null && newCustomers != null
                ? `${fmtNumber(newCustomers)} of ${fmtNumber(totalCustomers)} customers were new${
                    newCustomerShare != null
                      ? ` (${newCustomerShare.toFixed(1)}%)`
                      : ""
                  }`
                : "Insight pending"}
            </p>
          </div>
        </div>
        <div className={styles.customerRow}>
          <div className={styles.repeatMeter}>
            <span className={styles.repeatLabel}>Repeat Purchase Rate</span>
            <span className={styles.repeatValue}>{pct(repeatPurchaseRate)}</span>
            <div className={styles.repeatBar}>
              <div
                className={styles.repeatFill}
                style={{ width: `${repeatPct}%` }}
              />
            </div>
          </div>
          <div className={styles.statsRow}>
            <StatCard
              label="Total Customers"
              value={fmtNumber(totalCustomers)}
              accent="#22d3ee"
            />
            <StatCard
              label="New Customers"
              value={fmtNumber(newCustomers)}
              accent="#38bdf8"
            />
            <StatCard
              label="Returning Customers"
              value={fmtNumber(returningCustomers)}
              accent="#7c3aed"
            />
            <StatCard
              label="Avg Orders / Customer"
              value={formatDecimal(averageOrdersPerCustomer, 2)}
              accent="#10b981"
            />
            <StatCard
              label="Top 10% Revenue Share"
              value={pct(topCustomerRevenueShare)}
              accent="#f97316"
            />
          </div>
        </div>
        <div className={styles.detailList}>
          <Row
            label="Average Orders per Customer"
            value={formatDecimal(averageOrdersPerCustomer, 2)}
          />
          <Row
            label="Top 10% Revenue Share"
            value={pct(topCustomerRevenueShare)}
          />
        </div>
      </Section>
    </div>
  );
}
